import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import {
    DateIntervalSettingsParts,
    DatePrecision,
    DISPLAY_DATE_FORMATS,
    popperDateTimeOptions,
    READONLY_DATE_FORMATS,
} from '@bazis/form/models/form-element.types';
import { DateValidators } from '@bazis/form/validators/dateValidators';
import { SimpleData } from '@bazis/shared/models/srv.types';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';
import moment from 'moment';
import { EntityFormControl } from '@bazis/form/models/form.types';
import { Instance, createPopper } from '@popperjs/core';
import { BazisFormService } from '@bazis/form/services/form.service';
import { API_DATETIME_FORMAT } from '@bazis/configuration.service';
import { BazisControlValueAccessor } from '@bazis/form/components/control-value-accessor.component';
import { ConditionalValidators } from '@bazis/form/validators/conditionalValidators';

@UntilDestroy()
@Component({
    selector: 'bazis-input-dateinterval',
    templateUrl: './input-dateinterval.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class BazisInputDateintervalComponent
    extends BazisControlValueAccessor
    implements OnInit, AfterViewInit, OnChanges, OnDestroy
{
    @ViewChild('dateInputFrom') dateInputFrom: ElementRef;

    @ViewChild('dateInputTo') dateInputTo: ElementRef;

    @ViewChild('calendarPopperFrom') calendarPopperFrom: ElementRef;

    @ViewChild('calendarPopperTo') calendarPopperTo: ElementRef;

    @Input() fieldSettingsParts: DateIntervalSettingsParts = null;

    // заголовок поля от
    @Input() fromTitleKey: string = null;

    // заголовок поля до
    @Input() toTitleKey: string = null;

    // минимальная дата
    @Input() minDate: string = null;

    // максимальная дата
    @Input() maxDate: string = null;

    // параметры для from title
    @Input() fromTitleParams: any = null;

    // параметры для to title
    @Input() toTitleParams: any = null;

    @Input() inputFormat: string = API_DATETIME_FORMAT;

    @Input() precision: DatePrecision = 'days';

    @Input() requiredFrom$: Observable<boolean> = null;

    @Input() requiredTo$: Observable<boolean> = null;

    @Input() defaultInterval: boolean = true;

    @Input() showIntervalCheckbox = true;

    // маркеры для календаря
    @Input() marks$: Observable<{ [index: number]: string }>;

    // настройки ширины полей, во всю ширину формы, или две трети
    @Input() widthForm: 'full' | 'two-thirds' = 'full';

    // место отображения иконки календаря
    @Input() placeIcon: 'left' | 'right' = 'right';

    disabled = false;

    value = new FormGroup({
        from: new FormControl(),
        to: new FormControl(),
    });

    isInterval = new FormControl(['interval']);

    calendarValue: Date[] = [null, null];

    displayFormat: string;

    displayMaskFormat: string;

    displayIntervalMaskFormat: string;

    readonlyFormat: string;

    showCalendarFrom = false;

    showCalendarTo = false;

    mask = '';

    intervalMask = '';

    isIntervalOptions: SimpleData[] = [
        {
            id: 'interval',
            nameKey: 'title.period',
        },
    ];

    instanceDatePopperFrom: Instance;

    instanceDatePopperTo: Instance;

    isClickOnPopperFrom = false;

    isClickOnPopperTo = false;

    datapickerViewConfig;

    ngOnInit() {
        this.datapickerViewConfig = this.configurationService.datapickerViewConfig;

        if (this.ngControl.control instanceof EntityFormControl) {
            BazisFormService.getPropertiesFromConfig(this, this.ngControl.control.$config);

            this.requiredFrom$ =
                this.ngControl.control.$config.fieldConfig[
                    this.fieldSettingsParts.fromDateFieldName
                ].required.$;
            this.requiredTo$ =
                this.ngControl.control.$config.fieldConfig[
                    this.fieldSettingsParts.toDateFieldName
                ].required.$;
        }

        this.displayFormat = DISPLAY_DATE_FORMATS[this.precision];
        this.readonlyFormat = READONLY_DATE_FORMATS[this.precision];

        this.value.valueChanges
            .pipe(
                tap(() => {
                    this.setCalendarValues();
                    this.setControlValue();
                }),
                untilDestroyed(this),
            )
            .subscribe();

        this.initValidators();

        this.value.statusChanges
            .pipe(
                debounceTime(0),
                tap((status) => {
                    if (status === 'VALID') {
                        let errors = this.ngControl.control.errors;
                        if (!errors) return;
                        delete errors.componentError;
                        errors = Object.keys(errors).length > 0 ? errors : null;
                        this.ngControl.control.setErrors(errors);
                    } else {
                        this.ngControl.control.setErrors({
                            ...this.ngControl.control.errors,
                            componentError: { ...this.value.errors },
                        });
                    }
                }),
                untilDestroyed(this),
            )
            .subscribe();
        this.isInterval.valueChanges
            .pipe(
                tap(() => {
                    const isInterval = this.isInterval.value.length > 0;
                    if (!isInterval) {
                        this.value.get('to').setValue(null);
                    }
                }),
                untilDestroyed(this),
            )
            .subscribe();

        this.writeValue(this.ngControl.control.value);
    }

    ngAfterViewInit() {
        if (this.dateInputFrom && this.calendarPopperFrom) {
            const referenceFrom = this.dateInputFrom.nativeElement;
            const popperFrom = this.calendarPopperFrom.nativeElement;

            this.instanceDatePopperFrom = createPopper(referenceFrom, popperFrom, {
                ...popperDateTimeOptions,
                placement: 'bottom-end',
            });
        }
        if (this.dateInputTo && this.calendarPopperTo) {
            const referenceTo = this.dateInputTo.nativeElement;
            const popperTo = this.calendarPopperTo.nativeElement;

            this.instanceDatePopperTo = createPopper(referenceTo, popperTo, {
                ...popperDateTimeOptions,
                placement: 'bottom-end',
            });
        }
    }

    ngOnDestroy() {
        if (this.instanceDatePopperFrom) this.instanceDatePopperFrom.destroy();
        if (this.instanceDatePopperTo) this.instanceDatePopperTo.destroy();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.minDate || changes.maxDate) {
            this.initValidators();
            this.value.updateValueAndValidity();
        }
    }

    initValidators() {
        const validators = [];
        const minMaxValidators = [];
        let requiredValidatorFrom = [];
        let requiredValidatorTo = [];
        if (this.ngControl.control instanceof EntityFormControl) {
            requiredValidatorFrom = [
                ConditionalValidators.required(
                    () =>
                        (this.ngControl.control as EntityFormControl).$config.fieldConfig[
                            this.fieldSettingsParts.fromDateFieldName
                        ].required._,
                ),
            ];
            requiredValidatorTo = [
                ConditionalValidators.required(
                    () =>
                        (this.ngControl.control as EntityFormControl).$config.fieldConfig[
                            this.fieldSettingsParts.toDateFieldName
                        ].required._,
                ),
            ];
        }
        if (this.isInterval.value.length > 0) {
            validators.push(
                DateValidators.intervalDateTwoControlValidatorFn('from', 'to', this.displayFormat),
            );
        }
        if (this.minDate) {
            minMaxValidators.push(
                DateValidators.minDateValidatorFn(this.displayFormat, this.precision, this.minDate),
            );
        }
        if (this.maxDate) {
            minMaxValidators.push(
                DateValidators.maxDateValidatorFn(this.displayFormat, this.precision, this.maxDate),
            );
        }
        this.value.get('from').setValidators([...minMaxValidators, ...requiredValidatorFrom]);
        this.value.get('to').setValidators([...minMaxValidators, ...requiredValidatorTo]);
        this.value.setValidators(validators);
    }

    getMoment(part: string) {
        const momentDate = moment(part, this.displayFormat, true);
        return momentDate && momentDate.isValid() ? momentDate : null;
    }

    getMomentValues() {
        return {
            from: this.value.value.from ? this.getMoment(this.value.value.from) : null,
            to: this.value.value.to ? this.getMoment(this.value.value.to) : null,
            fromPart: this.value.value.from,
            toPart: this.value.value.to,
        };
    }

    setCalendarValues() {
        const momentValues = this.getMomentValues();
        const values = [momentValues.from ? momentValues.from.toDate() : null];
        if (this.isInterval.value[0] === 'interval') {
            values.push(momentValues.to ? momentValues.to.toDate() : null);
        }
        this.calendarValue = values;
    }

    setControlValue() {
        const momentValues = this.getMomentValues();
        this.onChange({
            [this.fieldSettingsParts.fromDateFieldName]: momentValues.from
                ? momentValues.from.format(this.inputFormat)
                : null,
            [this.fieldSettingsParts.toDateFieldName]: momentValues.to
                ? momentValues.to.format(this.inputFormat)
                : null,
        });
    }

    public setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
        if (isDisabled) {
            this.value.disable();
        } else {
            this.value.enable();
        }
    }

    public writeValue(value): void {
        if (!this.ngControl.control) return;
        if (
            !value ||
            (!value[this.fieldSettingsParts.fromDateFieldName] &&
                !value[this.fieldSettingsParts.toDateFieldName])
        ) {
            if (!this.defaultInterval) {
                this.isInterval.setValue([]);
            }
            this.value.setValue({ from: null, to: null });
            this.setCalendarValues();
            return;
        }
        const from = value[this.fieldSettingsParts.fromDateFieldName];
        const to = value[this.fieldSettingsParts.toDateFieldName];

        if (!to && !this.defaultInterval) this.isInterval.setValue([]);

        this.value.setValue({
            from: from ? moment(from, this.inputFormat).format(this.displayFormat) : null,
            to: to ? moment(to, this.inputFormat).format(this.displayFormat) : null,
        });
        this.setCalendarValues();
    }

    setValueFromCalendar(value) {
        const displayValues = value.map((v) => (v ? moment(v).format(this.displayFormat) : null));
        if (!displayValues.length) {
            this.value.setValue({ from: null, to: null });

            return;
        }

        if (!this.isInterval.value?.length) {
            this.value.setValue({ from: displayValues[0], to: null });

            if (displayValues[0]) {
                this.showCalendarFrom = false;
                this.showCalendarTo = false;
            }
            return;
        }

        this.value.setValue({ from: displayValues[0], to: displayValues[1] });
        if (value[0] !== null && value[1] !== null && displayValues[0] !== displayValues[1]) {
            this.showCalendarFrom = false;
            this.showCalendarTo = false;
        }
    }

    openDateCalendarFrom(e) {
        this.showCalendarFrom = !this.showCalendarFrom;
        this.showCalendarTo = false;
        if (this.instanceDatePopperFrom) this.instanceDatePopperFrom.update();
        if (this.instanceDatePopperTo) this.instanceDatePopperTo.update();
    }

    openDateCalendarTo(e) {
        this.showCalendarTo = !this.showCalendarTo;
        this.showCalendarFrom = false;
        if (this.instanceDatePopperFrom) this.instanceDatePopperFrom.update();
        if (this.instanceDatePopperTo) this.instanceDatePopperTo.update();
    }

    clickPopperFrom() {
        this.isClickOnPopperFrom = true;
        this.isClickOnPopperTo = false;
    }

    clickPopperTo() {
        this.isClickOnPopperTo = true;
        this.isClickOnPopperFrom = true;
    }

    clickPopperFromOutside() {
        this.showCalendarFrom = false;
        this.isClickOnPopperFrom = false;
    }

    clickPopperToOutside() {
        this.showCalendarTo = false;
        this.isClickOnPopperTo = false;
    }

    onClearFrom() {
        this.value.get('from').setValue(null);
    }

    onClearTo() {
        this.value.get('to').setValue(null);
    }
}
